import React, { useState, useEffect } from "react";
import { Workshop, Technic } from "../types";
import { workshopService, technicService } from "../services/dataService";
import { turkishIncludes } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import WorkshopCostsModal from "../components/WorkshopCostsModal";
import { usePermissions } from "../context/PermissionContext";
import "./Workshops.css";

type TabType = "workshops" | "technics";

const Workshops: React.FC = () => {
  const { has } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabType>("workshops");
  const [costModalWorkshop, setCostModalWorkshop] = useState<Workshop | null>(
    null,
  );
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [technics, setTechnics] = useState<Technic[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Teknik modal states
  const [isTechnicModalOpen, setIsTechnicModalOpen] = useState(false);
  const [editingTechnic, setEditingTechnic] = useState<Technic | null>(null);
  const [technicFormData, setTechnicFormData] = useState({
    name: "",
    category: "",
    description: "",
    workshopId: "",
  });

  // İlişki (bağlantı) modal state'leri
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [selectedWorkshopForConnections, setSelectedWorkshopForConnections] =
    useState<Workshop | null>(null);
  const [workshopConnections, setWorkshopConnections] = useState<any[]>([]);
  const [connSearch, setConnSearch] = useState("");
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    displayOrder: 0,
  });

  // Alert ve Confirm Modal state'leri
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [workshopsData, technicsData] = await Promise.all([
        workshopService.getAll(),
        technicService.getAll(),
      ]);

      // Backend zaten sıralı döndürüyor ama emin olmak için:
      const sortedData = [...workshopsData].sort((a, b) => {
        const orderA = a.displayOrder ?? 999;
        const orderB = b.displayOrder ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name, "tr");
      });
      setWorkshops(sortedData);
      setTechnics(technicsData);
    } catch (error) {
      console.error("❌ Failed to load data:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadWorkshops = loadData; // Geriye dönük uyumluluk için

  const openAddModal = () => {
    setEditingWorkshop(null);
    setFormData({
      name: "",
      location: "",
      displayOrder: 0,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (workshop: Workshop) => {
    setEditingWorkshop(workshop);
    setFormData({
      name: workshop.name,
      location: workshop.location || "",
      displayOrder: workshop.displayOrder || 0,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingWorkshop(null);
    setFormData({
      name: "",
      location: "",
      displayOrder: 0,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Atölye adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      const workshopData = {
        name: formData.name.trim(),
        location: formData.location.trim() || undefined,
        displayOrder: formData.displayOrder || 0,
      };

      if (editingWorkshop) {
        // Update
        await workshopService.update(editingWorkshop.workshopId, {
          ...workshopData,
          workshopId: editingWorkshop.workshopId,
        });
        setAlertModal({
          isOpen: true,
          message: "Atölye başarıyla güncellendi!",
          type: "success",
        });
      } else {
        // Create
        await workshopService.create(workshopData);
        setAlertModal({
          isOpen: true,
          message: "Atölye başarıyla oluşturuldu!",
          type: "success",
        });
      }

      closeModal();
      loadWorkshops();
    } catch (error: any) {
      console.error("❌ Failed to save workshop:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Atölye kaydedilemedi!";
      setAlertModal({ isOpen: true, message: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (workshop: Workshop) => {
    setConfirmModal({
      isOpen: true,
      message: `"${workshop.name}" atölyesini silmek istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await workshopService.delete(workshop.workshopId);
          setAlertModal({
            isOpen: true,
            message: "Atölye başarıyla silindi!",
            type: "success",
          });
          loadWorkshops();
        } catch (error: any) {
          console.error("❌ Failed to delete workshop:", error);
          setAlertModal({
            isOpen: true,
            message: "Atölye silinemedi!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const openConnectionModal = async (workshop: Workshop) => {
    setSelectedWorkshopForConnections(workshop);
    setIsConnectionModalOpen(true);
    setConnSearch("");
    await loadWorkshopConnections(workshop.workshopId);
  };

  const closeConnectionModal = () => {
    setIsConnectionModalOpen(false);
    setSelectedWorkshopForConnections(null);
    setWorkshopConnections([]);
    setConnSearch("");
  };

  const loadWorkshopConnections = async (workshopId: string) => {
    try {
      const connections = await workshopService.getConnections(workshopId);
      setWorkshopConnections(connections || []);
    } catch (error) {
      console.error("❌ Failed to load connections:", error);
      setWorkshopConnections([]);
    }
  };

  // Tek dokunuşla ilişki aç/kapat (anında uygulanır, sessiz)
  const toggleConnection = async (workshop: Workshop) => {
    if (!selectedWorkshopForConnections) return;
    const existing = workshopConnections.find(
      (c: any) => c.targetWorkshop?.workshopId === workshop.workshopId,
    );
    try {
      setTogglingId(workshop.workshopId);
      if (existing) {
        await workshopService.removeConnection(
          selectedWorkshopForConnections.workshopId,
          existing.connectionId,
        );
      } else {
        await workshopService.addConnection(
          selectedWorkshopForConnections.workshopId,
          { targetWorkshopId: workshop.workshopId, notes: "" },
        );
      }
      await loadWorkshopConnections(selectedWorkshopForConnections.workshopId);
    } catch (error: any) {
      console.error("❌ Failed to toggle connection:", error);
      const msg =
        error.response?.data || error.message || "Bağlantı güncellenemedi!";
      setAlertModal({ isOpen: true, message: msg, type: "error" });
    } finally {
      setTogglingId(null);
    }
  };

  // Teknik işlemleri
  const openTechnicModal = () => {
    setEditingTechnic(null);
    setTechnicFormData({
      name: "",
      category: "",
      description: "",
      workshopId: "",
    });
    setIsTechnicModalOpen(true);
  };

  const openEditTechnicModal = (technic: Technic) => {
    setEditingTechnic(technic);
    setTechnicFormData({
      name: technic.name,
      category: technic.category || "",
      description: technic.description || "",
      workshopId: technic.workshopId || "",
    });
    setIsTechnicModalOpen(true);
  };

  const closeTechnicModal = () => {
    setIsTechnicModalOpen(false);
    setEditingTechnic(null);
    setTechnicFormData({
      name: "",
      category: "",
      description: "",
      workshopId: "",
    });
  };

  const handleTechnicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!technicFormData.name.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Teknik adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      // Convert empty strings to undefined for nullable Guid fields
      const cleanedData = {
        ...technicFormData,
        workshopId: technicFormData.workshopId || undefined,
        category: technicFormData.category || undefined,
        description: technicFormData.description || undefined,
      };

      if (editingTechnic) {
        await technicService.update(editingTechnic.technicId, cleanedData);
        setAlertModal({
          isOpen: true,
          message: "Teknik başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await technicService.create(cleanedData);
        setAlertModal({
          isOpen: true,
          message: "Teknik başarıyla oluşturuldu!",
          type: "success",
        });
      }

      closeTechnicModal();
      loadData();
    } catch (error: any) {
      console.error("❌ Failed to save technic:", error);
      setAlertModal({
        isOpen: true,
        message: "Teknik kaydedilemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTechnicDelete = async (technic: Technic) => {
    setConfirmModal({
      isOpen: true,
      message: `"${technic.name}" tekniğini silmek istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await technicService.delete(technic.technicId);
          setAlertModal({
            isOpen: true,
            message: "Teknik başarıyla silindi!",
            type: "success",
          });
          loadData();
        } catch (error: any) {
          console.error("❌ Failed to delete technic:", error);
          setAlertModal({
            isOpen: true,
            message: "Teknik silinemedi!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const filteredWorkshops = workshops.filter(
    (workshop) =>
      turkishIncludes(workshop.name, searchTerm) ||
      turkishIncludes(workshop.location || "", searchTerm) ||
      turkishIncludes(workshop.contactPerson || "", searchTerm),
  );

  const filteredTechnics = technics.filter(
    (technic) =>
      turkishIncludes(technic.name, searchTerm) ||
      turkishIncludes(technic.category || "", searchTerm) ||
      turkishIncludes(technic.description || "", searchTerm),
  );

  if (loading && workshops.length === 0) {
    return <PageLoader message="Atölyeler yükleniyor..." />;
  }

  return (
    <div className="workshops-container">
      <div className="workshops-header">
        <div className="workshops-header-content">
          <div className="ws-breadcrumb">
            <span>Tanımlamalar</span>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
            <span className="active">Atölye ve Teknikler</span>
          </div>
          <h1>
            <div className="workshops-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21h18M5 21V8l6-3v3l6-3v16M9 21v-4h4v4" />
              </svg>
            </div>
            Atölye ve Teknikler
          </h1>
          <p className="workshops-subtitle">
            {activeTab === "workshops"
              ? `Toplam ${workshops.length} atölye kayıtlı`
              : `Toplam ${technics.length} teknik kayıtlı`}
          </p>
        </div>

        {has("workshops:create") && (
          <button
            className="btn btn-primary"
            onClick={activeTab === "workshops" ? openAddModal : openTechnicModal}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            {activeTab === "workshops" ? "Yeni Atölye" : "Yeni Teknik"}
          </button>
        )}
      </div>

      {/* Sekme navigasyonu */}
      <div className="ws-tabs">
        <button
          className={`ws-tab ${activeTab === "workshops" ? "active" : ""}`}
          onClick={() => setActiveTab("workshops")}
        >
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18M5 21V8l6-3v3l6-3v16M9 21v-4h4v4" />
          </svg>
          Atölyeler
          <span className="ws-tab-count">{workshops.length}</span>
        </button>
        <button
          className={`ws-tab ${activeTab === "technics" ? "active" : ""}`}
          onClick={() => setActiveTab("technics")}
        >
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L3 18.3V21h2.7l6.4-6.4a4 4 0 0 0 5.6-5.6l-2.6 2.6-2-2 2.6-2.3z" />
          </svg>
          Teknikler
          <span className="ws-tab-count">{technics.length}</span>
        </button>
      </div>

      <div className="workshops-search-bar">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#a0aec0">
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
        </svg>
        <input
          type="text"
          placeholder={
            activeTab === "workshops"
              ? "Atölye adı, lokasyon veya ilgili kişi ile ara..."
              : "Teknik adı, kategori veya açıklama ile ara..."
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {activeTab === "workshops" ? (
        <div className="workshops-grid">
          {filteredWorkshops.map((workshop) => (
            <div key={workshop.workshopId} className="workshop-card">
              <div className="workshop-card-header">
                <div>
                  <h3>
                    {workshop.displayOrder !== undefined &&
                      workshop.displayOrder !== 0 && (
                        <span className="ws-order-badge">
                          #{workshop.displayOrder}
                        </span>
                      )}
                    {workshop.name}
                  </h3>
                  {workshop.location && (
                    <div className="workshop-location">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                      {workshop.location}
                    </div>
                  )}
                </div>
              </div>

              <div className="workshop-card-actions">
                <button
                  className="btn btn-sm btn-soft"
                  onClick={() => setCostModalWorkshop(workshop)}
                  title="Maliyet Yönetimi"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                  </svg>
                  Maliyet
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => openConnectionModal(workshop)}
                  title="Bağlantılar"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
                  </svg>
                  Bağlantılar
                </button>
                {has("workshops:update") && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => openEditModal(workshop)}
                  title="Düzenle"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                  </svg>
                  Düzenle
                </button>
                )}
                {has("workshops:delete") && (
                <button
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => handleDelete(workshop)}
                  title="Sil"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                  </svg>
                  Sil
                </button>
                )}
              </div>
            </div>
          ))}

          {filteredWorkshops.length === 0 && (
            <div className="workshops-empty-state">
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2L2 7v10c0 5.5 3.8 10.7 10 12 6.2-1.3 10-6.5 10-12V7l-10-4zm0 16.7c-4.8-1-8-5.1-8-9.4V8.5l8-3.2 8 3.2v1.8c0 4.3-3.2 8.4-8 9.4z" />
              </svg>
              <h3>Atölye bulunamadı</h3>
              <p>
                {searchTerm
                  ? "Arama kriteri ile eşleşen atölye bulunamadı."
                  : "Henüz hiç atölye eklenmemiş. Yeni atölye ekleyerek başlayın."}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Teknikler listesi */
        <div className="ws-technic-list">
          {filteredTechnics.map((technic) => (
            <div key={technic.technicId} className="ws-technic-row">
              <div className="ws-technic-info">
                <div className="ws-technic-head">
                  <h3 className="ws-technic-name">{technic.name}</h3>
                  {technic.category && (
                    <span className="ws-chip ws-chip-cat">
                      {technic.category}
                    </span>
                  )}
                  {technic.workshop && (
                    <span className="ws-chip ws-chip-ws">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 21h18M5 21V8l6-3v3l6-3v16" />
                      </svg>
                      {technic.workshop.name}
                    </span>
                  )}
                </div>
                {technic.description && (
                  <p className="ws-technic-desc">{technic.description}</p>
                )}
              </div>

              <div className="ws-technic-actions">
                {has("workshops:update") && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => openEditTechnicModal(technic)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                  Düzenle
                </button>
                )}
                {has("workshops:delete") && (
                <button
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => handleTechnicDelete(technic)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Sil
                </button>
                )}
              </div>
            </div>
          ))}

          {filteredTechnics.length === 0 && (
            <div className="workshops-empty-state">
              <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L3 18.3V21h2.7l6.4-6.4a4 4 0 0 0 5.6-5.6l-2.6 2.6-2-2 2.6-2.3z" />
              </svg>
              <h3>Teknik bulunamadı</h3>
              <p>
                {searchTerm
                  ? "Arama kriteri ile eşleşen teknik bulunamadı."
                  : "Henüz hiç teknik eklenmemiş. Yeni teknik ekleyerek başlayın."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="workshops-modal-overlay">
          <div className="workshops-modal-content">
            <div className="workshops-modal-header">
              <div className="ws-modal-title">
                <span className="ws-modal-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18M5 21V8l6-3v3l6-3v16M9 21v-4h4v4" />
                  </svg>
                </span>
                <h2>{editingWorkshop ? "Atölye Düzenle" : "Yeni Atölye Ekle"}</h2>
              </div>
              <button className="workshops-modal-close" onClick={closeModal} aria-label="Kapat">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="workshops-modal-form">
              <div className="workshops-form-group">
                <label>Atölye Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Örn: Ana Üretim Atölyesi"
                  required
                />
              </div>

              <div className="workshops-form-group">
                <label>Lokasyon</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  placeholder="Örn: İstanbul, Türkiye"
                />
              </div>

              <div className="workshops-form-group">
                <label>Sıralama (DisplayOrder)</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="0"
                  min="0"
                />
                <small className="ws-form-hint">
                  Küçük değerler önce görünür. Varsayılan: 0
                </small>
              </div>

              <div className="workshops-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Teknik Modalı */}
      {isTechnicModalOpen && (
        <div className="workshops-modal-overlay">
          <div className="workshops-modal-content">
            <div className="workshops-modal-header">
              <div className="ws-modal-title">
                <span className="ws-modal-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L3 18.3V21h2.7l6.4-6.4a4 4 0 0 0 5.6-5.6l-2.6 2.6-2-2 2.6-2.3z" />
                  </svg>
                </span>
                <h2>{editingTechnic ? "Teknik Düzenle" : "Yeni Teknik Ekle"}</h2>
              </div>
              <button
                className="workshops-modal-close"
                onClick={closeTechnicModal}
                aria-label="Kapat"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <form
              onSubmit={handleTechnicSubmit}
              className="workshops-modal-form"
            >
              <div className="workshops-form-group">
                <label>Teknik Adı *</label>
                <input
                  type="text"
                  value={technicFormData.name}
                  onChange={(e) =>
                    setTechnicFormData({
                      ...technicFormData,
                      name: e.target.value,
                    })
                  }
                  placeholder="Örn: Dijital Baskı"
                  required
                />
              </div>

              <div className="workshops-form-group">
                <label>Kategori</label>
                <input
                  type="text"
                  value={technicFormData.category}
                  onChange={(e) =>
                    setTechnicFormData({
                      ...technicFormData,
                      category: e.target.value,
                    })
                  }
                  placeholder="Örn: Baskı Teknikleri"
                />
              </div>

              <div className="workshops-form-group">
                <label>Atölye</label>
                <select
                  className="ws-select"
                  value={technicFormData.workshopId}
                  onChange={(e) =>
                    setTechnicFormData({
                      ...technicFormData,
                      workshopId: e.target.value,
                    })
                  }
                >
                  <option value="">-- Atölye Seçiniz --</option>
                  {workshops.map((ws) => (
                    <option key={ws.workshopId} value={ws.workshopId}>
                      {ws.name}
                    </option>
                  ))}
                </select>
                <small className="ws-form-hint">
                  Opsiyonel: Bu tekniğin hangi atölyede kullanıldığını
                  belirtebilirsiniz.
                </small>
              </div>

              <div className="workshops-form-group">
                <label>Açıklama</label>
                <textarea
                  value={technicFormData.description}
                  onChange={(e) =>
                    setTechnicFormData({
                      ...technicFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Teknik hakkında kısa bir açıklama..."
                  rows={4}
                />
              </div>

              <div className="workshops-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeTechnicModal}
                  disabled={loading}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? "Kaydediliyor..."
                    : editingTechnic
                      ? "Güncelle"
                      : "Oluştur"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Connection Modal */}
      {isConnectionModalOpen && selectedWorkshopForConnections && (
        <div className="workshops-modal-overlay">
          <div
            className="workshops-modal-content"
            style={{ maxWidth: "37.5rem" }}
          >
            <div className="workshops-modal-header">
              <div className="ws-modal-title">
                <span className="ws-modal-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
                  </svg>
                </span>
                <h2>{selectedWorkshopForConnections.name} — Bağlantılar</h2>
              </div>
              <button
                className="workshops-modal-close"
                onClick={closeConnectionModal}
                aria-label="Kapat"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="workshops-modal-form">
              <p className="ws-rel-intro">
                Bu atölyeyle ilişkili atölyeleri seçin. Bir karta tıklayarak
                bağlayın; tekrar tıklayarak bağlantıyı kaldırın.
              </p>

              {(() => {
                const others = workshops.filter(
                  (w) =>
                    w.workshopId !== selectedWorkshopForConnections.workshopId,
                );
                const isLinked = (id: string) =>
                  workshopConnections.some(
                    (c: any) => c.targetWorkshop?.workshopId === id,
                  );
                const connectedCount = others.filter((w) =>
                  isLinked(w.workshopId),
                ).length;
                const visible = others.filter(
                  (w) =>
                    turkishIncludes(w.name, connSearch) ||
                    turkishIncludes(w.location || "", connSearch),
                );

                if (others.length === 0) {
                  return (
                    <div className="ws-conn-empty">
                      <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
                      </svg>
                      <p>Bağlanabilecek başka atölye yok</p>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="ws-rel-toolbar">
                      <div className="ws-rel-search">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        <input
                          type="text"
                          placeholder="Atölye ara..."
                          value={connSearch}
                          onChange={(e) => setConnSearch(e.target.value)}
                        />
                      </div>
                      <span className="ws-rel-count">
                        <b>{connectedCount}</b> / {others.length} bağlı
                      </span>
                    </div>

                    {visible.length === 0 ? (
                      <div className="ws-conn-empty">
                        <p>Aramayla eşleşen atölye bulunamadı</p>
                      </div>
                    ) : (
                      <div className="ws-rel-grid">
                        {visible.map((w) => {
                          const linked = isLinked(w.workshopId);
                          const busy = togglingId === w.workshopId;
                          return (
                            <button
                              key={w.workshopId}
                              type="button"
                              className={`ws-rel-card ${linked ? "connected" : ""}`}
                              onClick={() => toggleConnection(w)}
                              disabled={busy}
                              title={linked ? "Bağlantıyı kaldır" : "Bağla"}
                            >
                              <span className="ws-rel-check">
                                {busy ? (
                                  <span className="ws-rel-spin" />
                                ) : linked ? (
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                )}
                              </span>
                              <span className="ws-rel-info">
                                <span className="ws-rel-name">{w.name}</span>
                                {w.location && (
                                  <span className="ws-rel-loc">
                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                    {w.location}
                                  </span>
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="workshops-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={closeConnectionModal}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Atölye maliyet yönetimi modalı */}
      {costModalWorkshop && (
        <WorkshopCostsModal
          isOpen={!!costModalWorkshop}
          workshopId={costModalWorkshop.workshopId}
          workshopName={costModalWorkshop.name}
          onClose={() => setCostModalWorkshop(null)}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default Workshops;
